/**
 * O hotel NOMEADO pelo cliente, casado contra títulos de oferta (#3729). Três
 * leituras usam a mesma régua de palavras: a rede do `postParse` do agente
 * (`mapper/named_hotel.ts`), a busca do agente que nomeia um hotel ("Hotel para
 * las vegas treasure island…") e a busca do CARTÃO pelo BFF (#3791: o "Tentar
 * novamente" do cartão de erro não passa pelo agente) — as duas buscas trazem o
 * hotel pedido para o 1º lugar em vez de deixá-lo como mera âncora de
 * proximidade. Mora aqui, zero-React, para agente e BFF lerem a MESMA régua.
 */
import type { ChatUiHotelOffer, HotelPlannerPlace } from '../hotel_planner/hotel_planner_schema.js';
import { hotelDistanceKm } from './haversine.js';

/** Palavras que dizem o TIPO do estabelecimento, não QUAL — nas três línguas
 *  do catálogo — mais as PARTÍCULAS que articulam o nome ("de", "le", "by").
 *  Lista explícita, nunca régua de tamanho: "W" (W Barcelona) é marca. */
const GENERIC_TITLE_WORDS = new Set([
  'hotel',
  'hotels',
  'hoteis',
  'hostel',
  'pousada',
  'resort',
  'inn',
  'suite',
  'suites',
  'apart',
  'aparthotel',
  'apartments',
  'apartamentos',
  'boutique',
  'the',
  'and',
  'los',
  'las',
  'des',
  'del',
  'de',
  'da',
  'do',
  'dos',
  'das',
  'le',
  'la',
  'les',
  'el',
  'du',
  'by',
  'em',
  'no',
  'na',
  'e',
  'y',
  'a',
  'o',
]);

/** Palavras inteiras, minúsculas e sem diacríticos. `NFD` separa o acento do
 *  caractere-base; a regex remove a faixa de diacríticos combinantes. */
export function titleTokens(text: string): string[] {
  return text
    .normalize('NFD')
    .replaceAll(/[̀-ͯ]/g, '')
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter((token) => token.length > 0);
}

/** As palavras do título que dizem QUAL hotel é — o resto ("Hotel", "de") não
 *  identifica nada e não pode nem exigir nem casar. */
export function distinctiveTokens(title: string): string[] {
  return titleTokens(title).filter((token) => !GENERIC_TITLE_WORDS.has(token));
}

/** Uma letra trocada, sobrando ou faltando ("residense", "esplanda") — só em
 *  palavra de 5+ letras, onde um erro de digitação não vira outra palavra. */
function withinOneEdit(a: string, b: string): boolean {
  if (a === b) return true;
  if (Math.min(a.length, b.length) < 5 || Math.abs(a.length - b.length) > 1) return false;
  let i = 0;
  let j = 0;
  let edits = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      i += 1;
      j += 1;
      continue;
    }
    edits += 1;
    if (edits > 1) return false;
    if (a.length > b.length) i += 1;
    else if (b.length > a.length) j += 1;
    else {
      i += 1;
      j += 1;
    }
  }
  return edits + (a.length - i) + (b.length - j) <= 1;
}

function covers(words: readonly string[], wanted: readonly string[]): boolean {
  return wanted.every((token) => words.some((word) => withinOneEdit(word, token)));
}

interface NamedCandidate {
  offerId: string;
  title: string;
  city?: string;
}

/** A oferta que o NOME designa: toda palavra distintiva do nome aparece no
 *  título (a CIDADE da oferta não conta — "em Las Vegas" fala do lugar). Vários
 *  títulos casando ("Hotel Esplanada Belo Horizonte" e o homônimo "… - Proximo
 *  a Estacao de Trem") desempata pelo que o nome cobre POR INTEIRO; sem
 *  desempate, `null` — um hotel escolhido errado é pior que nenhum. */
export function offerNamedBy<T extends NamedCandidate>(
  name: string,
  offers: readonly T[],
): T | null {
  const matches = offers.filter((offer) => {
    const city = new Set(titleTokens(offer.city ?? ''));
    const wanted = distinctiveTokens(name).filter((token) => !city.has(token));
    return wanted.length > 0 && covers(titleTokens(offer.title), wanted);
  });
  if (matches.length <= 1) return matches[0] ?? null;
  // Sem tolerância primeiro: "Treasure Island" contra "Treasure Island" e
  // "Treasury Island" é o primeiro, não empate (CodeRabbit #3735).
  const nameWords = titleTokens(name);
  const literal = matches.filter((offer) => {
    const title = new Set(titleTokens(offer.title));
    return distinctiveTokens(name).every((token) => title.has(token));
  });
  if (literal.length === 1) return literal[0] ?? null;
  const exact = matches.filter((offer) => covers(nameWords, distinctiveTokens(offer.title)));
  return exact.length === 1 ? (exact[0] ?? null) : null;
}

/** Até onde da âncora o hotel nomeado ainda É o lugar buscado. */
const NAMED_ANCHOR_MAX_KM = 0.2;

/**
 * A busca cujo LUGAR é um hotel: o hotel pedido à frente da lista, ANTES de
 * qualquer corte — o fornecedor busca PERTO do lugar e, na ordem dele, o hotel
 * pedido caía fora do cartão (#3729 no agente, #3791 no BFF, que corta no teto
 * do cartão).
 *
 * Duas provas juntas, porque bairro também vira título ("Paulista, São Paulo"
 * × "VOLL Hotel Paulista"): um trecho do lugar, em ordem, NOMEIA a oferta
 * (`offerNamedBy`) E ela está NA âncora da busca — quando o lugar é o próprio
 * hotel, ele fica a metros dela (`hotelDistanceKm`, a régua do card). `card`
 * projeta a oferta para essa régua. Sem hotel no lugar, a lista passa intacta.
 */
export function pinPlaceHotel<T extends NamedCandidate>(
  offers: readonly T[],
  place: { readonly text: string | undefined; readonly details: HotelPlannerPlace | undefined },
  card: (offer: T) => ChatUiHotelOffer | null,
): { offers: T[]; named: T | null } {
  // Cada trecho do lugar, em ordem: o hotel vem antes da cidade por convenção,
  // mas o Haiku 5.5 escrevia "Las Vegas, Treasure Island" (prova 09/10). Um
  // trecho que é só a cidade nunca casa — `offerNamedBy` tira as palavras da
  // cidade da oferta —, e juntar os trechos quebraria "<hotel>, <bairro>, …".
  const names = (place.text ?? '')
    .split(/[,/]/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  if (names.length === 0) return { offers: [...offers], named: null };
  const atAnchor = offers.filter((offer) => {
    const projected = card(offer);
    const km = projected ? hotelDistanceKm(projected, place.details) : undefined;
    return km !== undefined && km <= NAMED_ANCHOR_MAX_KM;
  });
  const named = names.reduce<T | null>(
    (found, name) => found ?? offerNamedBy(name, atAnchor),
    null,
  );
  if (!named) return { offers: [...offers], named: null };
  return { offers: [named, ...offers.filter((offer) => offer !== named)], named };
}
